import { useEffect, useState } from 'react';
import api from '../api/api';
import styled from 'styled-components';

const Container = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  @media (min-width: 1024px) {
    padding: 6rem 10% 4rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CategorySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const CategoryTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #f8fafc;
  position: relative;
  padding-bottom: 0.5rem;
  width: fit-content;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background: linear-gradient(90deg, #6366f1, #06b6d4);
    border-radius: 99px;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.5rem;
`;

const SkillCard = styled.div`
  background: rgba(30, 41, 59, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-left: 4px solid ${(props) => props.cor || '#6366f1'};
  color: #f8fafc;
  padding: 1.25rem 1.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    background: rgba(30, 41, 59, 0.7);
    border-color: rgba(255, 255, 255, 0.1) rgba(255, 255, 255, 0.1) rgba(255, 255, 255, 0.1) ${(props) => props.cor || '#6366f1'};
    box-shadow: 0 10px 20px -8px ${(props) => props.cor || '#6366f1'}80;
  }
`;

const Icon = styled.span`
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  padding: 8px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const SkillInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SkillName = styled.strong`
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.2px;
`;

const SkillDetail = styled.span`
  font-size: 0.8rem;
  color: #94a3b8;
`;

const Empty = styled.p`
  text-align: center;
  color: #64748b;
  font-size: 1.1rem;
  padding: 4rem 2rem;
  background: rgba(30, 41, 59, 0.2);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.03);
`;

const Error = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #ef4444;
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
  font-weight: 600;
`;

function Skills() {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    async function dadosSkills() {
      try {
        const resposta = await api.get('/api/skills');
        setDados(resposta.data || []);
        setErro(null);
      } catch (err) {
        console.error('Erro ao carregar skills:', err);
        setErro('Não foi possível carregar as skills');
      } finally {
        setLoading(false);
      }
    }
    dadosSkills();
  }, []);

  if (loading) {
    return (
      <Container>
        <Empty>Carregando...</Empty>
      </Container>
    );
  }

  if (erro) {
    return (
      <Container>
        <Error>{erro}</Error>
      </Container>
    );
  }

  if (dados.length === 0) {
    return (
      <Container>
        <Title>Habilidades</Title>
        <Empty>Nenhuma skill cadastrada ainda.</Empty>
      </Container>
    );
  }

  // Agrupa skills por categoria
  const categorias = dados.reduce((acc, skill) => {
    const categoria = skill.categoria || 'Outros';
    if (!acc[categoria]) {
      acc[categoria] = [];
    }
    acc[categoria].push(skill);
    return acc;
  }, {});

  return (
    <Container id="skills">
      <Title>Habilidades</Title>

      {Object.keys(categorias).map((categoria) => (
        <CategorySection key={categoria}>
          <CategoryTitle>{categoria}</CategoryTitle>
          
          <SkillsGrid>
            {categorias[categoria].map((skill) => (
              <SkillCard key={skill.id} cor={skill.cor}>
                {skill.icone && <Icon>{skill.icone}</Icon>}
                
                <SkillInfo>
                  <SkillName>{skill.nomehabilidade}</SkillName>
                  {(skill.nivel || skill.anos_experiencia) && (
                    <SkillDetail>
                      {skill.nivel}
                      {skill.nivel && skill.anos_experiencia && ' • '}
                      {skill.anos_experiencia && `${skill.anos_experiencia} ${skill.anos_experiencia === '1' ? 'ano' : 'anos'}`}
                    </SkillDetail>
                  )}
                </SkillInfo>
              </SkillCard>
            ))}
          </SkillsGrid>
        </CategorySection>
      ))}
    </Container>
  );
}

export default Skills;
